import { formatDistanceToNow } from "date-fns";
import { Calendar, Folder, User } from "lucide-react";

import type { Project } from "@cohesive/api-entities";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardDescription,
  BaseEntityCardExternalLinkAction,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardMentionAction,
  BaseEntityCardMetadata,
  BaseEntityCardMetadataItem,
  BaseEntityCardProps,
  BaseEntityCardTags,
  BaseEntityCardTitle,
} from "../BaseCard";

export { Project };

type ProjectCardProps = Project &
  BaseEntityCardProps & {
    owner_login?: string;
  };

// Avatar component for ProjectCard
export function ProjectCardAvatar({
  className,
  style,
}: Partial<ProjectCardProps>) {
  return (
    <BaseEntityCardIcon className={className} style={style}>
      <Folder className="h-5 w-5 text-muted-foreground" />
    </BaseEntityCardIcon>
  );
}

export function ProjectCard({
  platform,
  name,
  description = "",
  lead,
  web_link,
  updated_at,
  owner_login,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
}: ProjectCardProps) {
  const formattedUpdatedDate = updated_at
    ? formatDistanceToNow(new Date(updated_at), { addSuffix: true })
    : "";

  const descriptionPreview =
    description && description.length > 100
      ? `${description.substring(0, 100)}...`
      : description;

  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={className}
      style={style}
    >
      <ProjectCardAvatar />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle platform={platform}>{name}</BaseEntityCardTitle>
          <BaseEntityCardActions>
            {web_link && (
              <BaseEntityCardExternalLinkAction
                href={web_link}
                tooltip="Open project"
              />
            )}
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={onMentionClick} />
            )}
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>

        <BaseEntityCardBody>
          {descriptionPreview && (
            <BaseEntityCardDescription className="line-clamp-2 text-foreground">
              {descriptionPreview}
            </BaseEntityCardDescription>
          )}

          <BaseEntityCardTags>
            {owner_login && (
              <span className="text-xs text-muted-foreground">
                {owner_login}
              </span>
            )}

            {lead?.name && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <User className="h-3 w-3" />
                <span>{lead.name}</span>
              </div>
            )}
          </BaseEntityCardTags>

          {formattedUpdatedDate && (
            <BaseEntityCardMetadata>
              <BaseEntityCardMetadataItem
                icon={<Calendar className="h-3 w-3" />}
              >
                Updated {formattedUpdatedDate}
              </BaseEntityCardMetadataItem>
            </BaseEntityCardMetadata>
          )}
        </BaseEntityCardBody>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
